import React, {useCallback, useState} from 'react';
import {Select} from 'antd';
import {debounce} from 'lodash';
import {useMount} from '../../../hooks/useMount';

const AutoComplete = ({
  form,
  name,
  propertyName,
  searchFunction,
  reverseSearchFunction,
  isLoading,
  setIsLoading,
  allowClear = true,
  onChange,
  value,
  placeholder,
  populateStore,
  renderOption,
  multiple,
  ...props
}) => {
  const [options, setOptions] = useState([]);
  const [lastResults, setLastResults] = useState([]);
  const [initialValueIsSet, setInitialValueIsSet] = useState(false);
  const [labeledValue, setLabeledValue] = useState([]);
  const {Option} = Select;

  const setFormValue = (newValue) => {
    if (form) {
      form.setFieldsValue({[name]: newValue});
    }
    if (onChange) {
      onChange(newValue);
    }
    if (populateStore && newValue) {
      if (multiple) {
        newValue.forEach((v) => populateStore(v));
      } else {
        populateStore(newValue);
      }
    }
  };

  const setValue = (option) => {
    if (multiple) {
      if (value) {
        setFormValue([...value, option.value]);
      } else {
        setFormValue([option.value]);
      }
      setLabeledValue([...labeledValue, option]);
    } else {
      setFormValue(option.value);
      setLabeledValue(option);
    }
  };

  const sendQuery = (query) => {
    setIsLoading(true);
    searchFunction({params: {q: query}}).then((result) => {
      if (result.length > 0) {
        const renderedOptions = result.map(renderOption);
        setOptions(renderedOptions);
        setLastResults(result);
        const exactMatch = renderedOptions.find((o) => o.label === query);
        if (exactMatch) {
          setValue(exactMatch);
        }
      } else {
        setFormValue(null);
        setOptions([]);
      }
    }).catch(() => {
      setFormValue(null);
      setOptions([]);
    }).finally(() => {
      setIsLoading(false);
    });
  };

  useMount(() => {
    if (reverseSearchFunction && !initialValueIsSet && value) {
      setInitialValueIsSet(true);
      if (multiple) {
        let results = [];
        value.forEach((val) => {
          const matchingResult = lastResults.find((r) => String(r[propertyName]) === String(val));
          if (matchingResult) results.push(matchingResult);
        });
        if (value.length > results.length) {
          results = reverseSearchFunction(value);
        }
        setLabeledValue(results.map(renderOption));
      } else {
        let result = lastResults.find((r) => String(r[propertyName]) === String(value));
        if (!result) {
          result = reverseSearchFunction(value);
        }
        setLabeledValue(renderOption(result));
      }
    }
    if (value === undefined) {
      setOptions([]);
      setLabeledValue([]);
    }
  }, [value]);

  const debouncedQuery = useCallback(debounce((q) => sendQuery(q), 100), []);

  const onSearch = (input) => {
    if (input) {
      if (!options.find((option) => option.value === input)) {
        debouncedQuery(input);
      }
    }
  };

  const onDeselect = (v) => {
    if (multiple) {
      setFormValue(value.filter((el) => el !== v.value));
      setLabeledValue(labeledValue.filter((el) => el.value !== v.value));
    } else {
      setFormValue(null);
      setLabeledValue({});
    }
  };

  const onClear = () => {
    if (multiple) {
      setFormValue([]);
      setLabeledValue([]);
    } else {
      setFormValue(null);
      setLabeledValue({});
    }
    setOptions([]);
  };

  return (
    <Select
      {...props}
      value={labeledValue}
      options={options}
      onSearch={onSearch}
      onSelect={(v, option) => setValue(option)}
      onDeselect={onDeselect}
      onClear={onClear}
      showArrow={false}
      filterOption={false}
      mode={multiple ? 'multiple' : null}
      showSearch
      allowClear={allowClear}
      labelInValue
    >
      {options.map((option) => <Option key={option.value} value={option.value}>{option.label}</Option>)}
    </Select>

  );
};
export default AutoComplete;
